"use client";

import Link from "next/link";
import type { ProductRecommendation } from "@/lib/api";
import type { ReportFollowupContext } from "@/lib/report-followup";
import type { ReportPlanId } from "./PaidReportCTA.types";
import {
  trackReportCheckoutRecoveryClicked,
  trackReportCheckoutRecovered,
  trackReportRemedyKitUpsellClicked,
} from "@/lib/analytics";
import { ReportRating } from "./ReportRating";

interface AbandonedPanelProps {
  planId: ReportPlanId;
  priceInr: number;
  source?: string;
  recoveryHref: string | null;
  analyticsContext?: {
    placement?: string;
    intent?: string;
    sourcePath?: string;
    customerProblem?: string;
    routeRankScore?: number;
    routeRankReason?: string;
    rankedOfferOrder?: string[];
    agentTraceId?: string;
  };
  onRetry: () => void;
}

export function AbandonedPanel({
  planId,
  priceInr,
  source,
  recoveryHref,
  analyticsContext,
  onRetry,
}: AbandonedPanelProps) {
  return (
    <div
      className="mt-4 rounded-lg p-4 text-left"
      style={{
        background: "color-mix(in srgb, var(--ish-accent) 6%, var(--ish-bg))",
        border: "1px solid color-mix(in srgb, var(--ish-accent) 18%, transparent)",
      }}
    >
      <p className="text-[13px] font-semibold" style={{ color: "var(--ish-dark)" }}>
        Checkout paused
      </p>
      <p className="mt-1 text-[12px] leading-relaxed" style={{ color: "var(--ish-text-muted)" }}>
        Continue the same report checkout, or email support if payment did not open correctly.
      </p>
      <button
        type="button"
        onClick={() => {
          trackReportCheckoutRecovered({
            plan_id: planId,
            price_inr: priceInr,
            source,
            placement: analyticsContext?.placement,
            intent: analyticsContext?.intent,
            source_path: analyticsContext?.sourcePath,
            customer_problem: analyticsContext?.customerProblem,
            route_rank_score: analyticsContext?.routeRankScore,
            route_rank_reason: analyticsContext?.routeRankReason,
            ranked_offer_order: analyticsContext?.rankedOfferOrder,
            agent_trace_id: analyticsContext?.agentTraceId,
          });
          onRetry();
        }}
        className="mt-3 rounded-lg px-3 py-2 text-[12px] font-semibold text-white"
        style={{ background: "var(--ish-accent)" }}
      >
        Continue report checkout
      </button>
      {recoveryHref && (
        <a
          href={recoveryHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            trackReportCheckoutRecoveryClicked({
              plan_id: planId,
              plan_type: "report",
              source,
              placement: analyticsContext?.placement,
              intent: analyticsContext?.intent,
              source_path: analyticsContext?.sourcePath,
              recovery_channel: "support_email",
            })
          }
          className="ml-2 mt-3 inline-flex rounded-lg px-3 py-2 text-[12px] font-semibold"
          style={{
            color: "var(--ish-accent)",
            border: "1px solid color-mix(in srgb, var(--ish-accent) 35%, transparent)",
          }}
        >
          Email support
        </a>
      )}
    </div>
  );
}

interface FollowupPanelProps {
  followup: ReportFollowupContext;
  downloadUrl: string;
  onAskChart: () => void;
}

export function FollowupPanel({ followup, downloadUrl, onAskChart }: FollowupPanelProps) {
  return (
    <div
      className="mt-4 rounded-lg p-4 text-left"
      style={{
        background: "color-mix(in srgb, var(--ish-accent) 8%, var(--ish-bg))",
        border: "1px solid color-mix(in srgb, var(--ish-accent) 22%, transparent)",
      }}
    >
      <p className="text-[13px] font-semibold" style={{ color: "var(--ish-dark)" }}>
        {followup.headline}
      </p>
      <p className="mt-1 text-[12px] leading-relaxed" style={{ color: "var(--ish-text-muted)" }}>
        {followup.body}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {/* downloadUrl is the /r/{token} reading page when a fulfillment link
            exists, or the direct PDF as fallback — label covers both. */}
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-lg px-3 py-2 text-[12px] font-semibold"
          style={{
            color: "var(--ish-accent)",
            border: "1px solid color-mix(in srgb, var(--ish-accent) 35%, transparent)",
          }}
        >
          Read your report
        </a>
        <button
          type="button"
          onClick={onAskChart}
          className="rounded-lg px-3 py-2 text-[12px] font-semibold text-white"
          style={{ background: "var(--ish-accent)" }}
        >
          Ask my chart
        </button>
      </div>
      <ReportRating orderId={followup.orderId} planId={followup.planId} />
      {followup.planId !== "brihat_kundali" && (
        <Link
          href="/reports/brihat-kundali/?from=post_purchase"
          className="mt-3 block rounded-lg px-3 py-2.5 text-center text-[12px] font-semibold text-white"
          style={{ background: "var(--ish-accent)" }}
        >
          Complete your reading — full Brihat Kundali, ₹399
        </Link>
      )}
      {/* Recurring upsell at the highest-intent moment (post-purchase). Secondary
          (outline) styling keeps the panel to one PRIMARY CTA (rule 39). Targets
          the 95%-one-and-done LTV gap from the 2026-06-14 revenue audit (L4). */}
      <Link
        href="/membership/?from=post_purchase"
        className="mt-2 block rounded-lg px-3 py-2.5 text-center text-[12px] font-semibold"
        style={{
          color: "var(--ish-accent)",
          border: "1px solid color-mix(in srgb, var(--ish-accent) 35%, transparent)",
        }}
      >
        Get a reading like this every month — Membership ₹299/mo
      </Link>
    </div>
  );
}

interface RemedyKitPanelProps {
  planId: ReportPlanId;
  source?: string;
  analyticsContext?: {
    intent?: string;
    sourcePath?: string;
    customerProblem?: string;
  };
  remedyKit: {
    href: string;
    products: ProductRecommendation[];
    orderId: string;
  };
}

export function RemedyKitPanel({
  planId,
  source,
  analyticsContext,
  remedyKit,
}: RemedyKitPanelProps) {
  return (
    <div
      className="mt-4 rounded-lg p-4 text-left"
      style={{
        background: "color-mix(in srgb, var(--ish-accent) 6%, var(--ish-bg))",
        border: "1px solid color-mix(in srgb, var(--ish-accent) 18%, transparent)",
      }}
    >
      <p className="text-[13px] font-semibold" style={{ color: "var(--ish-dark)" }}>
        Want the matched remedy items next?
      </p>
      <p className="mt-1 text-[12px] leading-relaxed" style={{ color: "var(--ish-text-muted)" }}>
        These products were matched to the same chart context. Use them only after reading the
        report and confirming the upay feels right.
      </p>
      <ul className="mt-3 space-y-1 text-[12px]" style={{ color: "var(--ish-text-muted)" }}>
        {remedyKit.products.map((product) => (
          <li key={product.sku}>
            <span className="font-semibold" style={{ color: "var(--ish-dark)" }}>
              {product.product_name}
            </span>
            {product.price_range ? ` - ${product.price_range}` : ""}
          </li>
        ))}
      </ul>
      <a
        href={remedyKit.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          trackReportRemedyKitUpsellClicked({
            plan_id: planId,
            order_id: remedyKit.orderId,
            source,
            placement: "post_report_remedy_kit",
            intent: analyticsContext?.intent,
            source_path: analyticsContext?.sourcePath,
            customer_problem: analyticsContext?.customerProblem,
            product_skus: remedyKit.products.map((product) => product.sku),
            product_count: remedyKit.products.length,
          })
        }
        className="mt-3 inline-flex rounded-lg px-3 py-2 text-[12px] font-semibold text-white"
        style={{ background: "var(--ish-accent)" }}
      >
        View remedy kit
      </a>
    </div>
  );
}
